import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Cake, Mail, MessageCircle, ChevronDown, ChevronUp, Gift, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const BirthdayReminder = () => {
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [sendingWish, setSendingWish] = useState(false);
  const [wishDialog, setWishDialog] = useState({ open: false, customer: null, channel: null });
  const [customMessage, setCustomMessage] = useState("");

  useEffect(() => {
    fetchBirthdays();
  }, []);

  const fetchBirthdays = async () => {
    try {
      const response = await axios.get(`${API}/customers/birthdays/upcoming?days_ahead=7`);
      setBirthdays(response.data.upcoming_birthdays || []);
    } catch (error) {
      console.error("Error fetching birthdays:", error);
    } finally {
      setLoading(false);
    }
  };

  const openWishDialog = (customer, channel) => {
    const companyName = "Bosveld Bou & Hardware"; // Could fetch from settings
    // Use the celebrant's name (which may be the secondary person on a
    // shared account) rather than the primary account holder's name.
    const celebrantName = customer.celebrant_name || customer.name;
    const defaultMsg = `🎂 Happy Birthday, ${celebrantName}! 🎉\n\nWishing you a wonderful day filled with joy and happiness. Thank you for being a valued customer of ${companyName}.\n\nWarm regards,\nThe ${companyName} Team`;
    setCustomMessage(defaultMsg);
    setWishDialog({ open: true, customer, channel });
  };

  const sendWish = async () => {
    if (!wishDialog.customer) return;
    
    setSendingWish(true);
    try {
      const response = await axios.post(`${API}/customers/birthdays/send-wish`, {
        customer_id: wishDialog.customer.id,
        channel: wishDialog.channel,
        custom_message: customMessage,
      });

      if (wishDialog.channel === "whatsapp" && response.data.whatsapp_link) {
        window.open(response.data.whatsapp_link, "_blank");
        toast.success("WhatsApp opened - send the birthday wish!");
      } else {
        toast.success("Birthday wish sent via email!");
      }
      
      setWishDialog({ open: false, customer: null, channel: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send birthday wish");
    } finally {
      setSendingWish(false);
    }
  };

  if (loading || birthdays.length === 0) {
    return null; // Don't show if no birthdays
  }

  const todaysBirthdays = birthdays.filter(b => b.is_today);
  const upcomingBirthdays = birthdays.filter(b => !b.is_today);

  return (
    <>
      <Card className="border-pink-200 bg-gradient-to-r from-pink-50 to-purple-50 mb-4" data-testid="birthday-reminder-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-pink-700" data-testid="birthday-reminder-title">
              <Cake className="w-5 h-5" />
              Birthday Reminders
              {todaysBirthdays.length > 0 && (
                <Badge className="bg-pink-500 animate-pulse" data-testid="birthday-today-badge">
                  <PartyPopper className="w-3 h-3 mr-1" />
                  {todaysBirthdays.length} TODAY!
                </Badge>
              )}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              data-testid="birthday-expand-toggle"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Today's Birthdays */}
          {todaysBirthdays.length > 0 && (
            <div className="mb-3" data-testid="todays-birthdays-section">
              <p className="text-xs font-semibold text-pink-600 mb-2">🎉 TODAY'S BIRTHDAYS</p>
              {todaysBirthdays.map((customer) => (
                <div
                  key={customer.id}
                  className="flex items-center justify-between p-2 bg-white rounded-lg border border-pink-200 mb-2"
                  data-testid={`birthday-row-${customer.id}`}
                >
                  <div>
                    <p className="font-semibold text-pink-800">
                      {customer.celebrant_name || customer.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {customer.celebrant_role === "secondary" && (
                        <span className="text-pink-600">on {customer.name}'s account • </span>
                      )}
                      {customer.account_number}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {customer.email && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-pink-300 text-pink-600 hover:bg-pink-100"
                        onClick={() => openWishDialog(customer, "email")}
                        data-testid={`send-email-wish-${customer.id}`}
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        Email
                      </Button>
                    )}
                    {(customer.whatsapp_number || customer.contact_number) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-green-300 text-green-600 hover:bg-green-100"
                        onClick={() => openWishDialog(customer, "whatsapp")}
                        data-testid={`send-whatsapp-wish-${customer.id}`}
                      >
                        <MessageCircle className="w-4 h-4 mr-1" />
                        WhatsApp
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Upcoming Birthdays */}
          {expanded && upcomingBirthdays.length > 0 && (
            <div data-testid="upcoming-birthdays-section">
              <p className="text-xs font-semibold text-purple-600 mb-2">📅 UPCOMING (Next 7 Days)</p>
              {upcomingBirthdays.map((customer) => (
                <div
                  key={customer.id}
                  className="flex items-center justify-between p-2 bg-white/70 rounded-lg border border-purple-100 mb-1"
                  data-testid={`birthday-row-${customer.id}`}
                >
                  <div>
                    <p className="font-medium text-purple-800">
                      {customer.celebrant_name || customer.name}
                      {customer.celebrant_role === "secondary" && (
                        <span className="text-xs text-purple-500 ml-1">(on {customer.name}'s account)</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      In {customer.days_until} day{customer.days_until !== 1 ? "s" : ""} • {customer.birthday_date?.substring(5, 10)}
                    </p>
                  </div>
                  <Gift className="w-4 h-4 text-purple-400" />
                </div>
              ))}
            </div>
          )}

          {!expanded && upcomingBirthdays.length > 0 && (
            <p className="text-xs text-purple-600">
              +{upcomingBirthdays.length} more birthday{upcomingBirthdays.length !== 1 ? "s" : ""} this week
            </p>
          )}
        </CardContent>
      </Card>

      {/* Send Wish Dialog */}
      <Dialog open={wishDialog.open} onOpenChange={(open) => !open && setWishDialog({ open: false, customer: null, channel: null })}>
        <DialogContent className="max-w-md" data-testid="birthday-wish-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cake className="w-5 h-5 text-pink-500" />
              Send Birthday Wish
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">
                Sending to: <strong>{wishDialog.customer?.celebrant_name || wishDialog.customer?.name}</strong>
                {wishDialog.customer?.celebrant_role === "secondary" && (
                  <span className="text-xs text-gray-500 ml-1">(on {wishDialog.customer?.name}'s account)</span>
                )}
              </p>
              <p className="text-xs text-gray-500">
                via {wishDialog.channel === "email" ? `Email (${wishDialog.customer?.email})` : `WhatsApp (${wishDialog.customer?.whatsapp_number || wishDialog.customer?.contact_number})`}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Message</label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={6}
                className="mt-1"
                data-testid="birthday-wish-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setWishDialog({ open: false, customer: null, channel: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={sendWish}
              disabled={sendingWish}
              className="bg-pink-500 hover:bg-pink-600"
              data-testid="send-birthday-wish-button"
            >
              {sendingWish ? "Sending..." : wishDialog.channel === "email" ? "Send Email" : "Open WhatsApp"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BirthdayReminder;
